import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import api, { fmt, fmtQty, fmtDate, daysUntil } from '../../utils/api';
import { notifyError, notifyMessage } from '../../utils/notify';
import { confirm } from '../../utils/confirm';
import * as XLSX from 'xlsx';

const PALLET_CBM = 1.0 * 1.2 * 2.05; // 2.46
const CONTAINER_40HC_CBM = 67.25; // 40' High Cube: 12.03 × 2.35 × 2.39m
const PALLETS_PER_40HC = 18; // 20 max, 10% buffer for imperfect loading
const fmtWeight = (kg) => kg >= 1000 ? `${(kg / 1000).toFixed(2)}t` : `${kg.toFixed(1)} kg`;


export default function LogisticsPage() {
  const { t, i18n } = useTranslation();
  const { hasRole } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set());
  const [editing, setEditing] = useState({});
  const [search, setSearch] = useState('');
  const [logTab, setLogTab] = useState('all');
  const [sortBy, setSortBy] = useState('arrival');
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [selectedLines, setSelectedLines] = useState(new Set());  // keys: `${orderNo}::${idx}`
  const [sortDir, setSortDir] = useState('asc');
  const [filterOrigin, setFilterOrigin] = useState('');
  const [filterType, setFilterType] = useState('');
  const canEdit = hasRole('admin', 'warehouse', 'brand_manager');

  const toggleSort = (field) => {
    if (sortBy === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortDir('asc'); }
  };

  useEffect(() => {
    api.get('/logistics').then(r => setOrders(r.data.orders || r.data || []))
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  const toggle = (no) => setExpanded(prev => {
    const next = new Set(prev);
    next.has(no) ? next.delete(no) : next.add(no);
    return next;
  });

  const updateField = (orderNo, field, value) => {
    setEditing(prev => ({
      ...prev,
      [orderNo]: {
        ...(prev[orderNo] || {}),
        [field]: value,
      }
    }));
  };

  const getField = (order, field) => {
    return editing[order.order_no]?.[field] ?? order[field === 'arrivalDate' ? 'arrival_date' : field === 'transportMode' ? 'transport_mode' : field] ?? '';
  };

  const saveEdit = async (orderNo, order) => {
    const ed = editing[orderNo] || {};
    const data = {
      arrivalDate: ed.arrivalDate ?? (order.arrival_date ? order.arrival_date.slice(0, 10) : ''),
      transportMode: ed.transportMode ?? order.transport_mode ?? '',
      notes: ed.notes ?? order.notes ?? '',
    };
    try {
      await api.put(`/logistics/${orderNo}`, data);
      setOrders(prev => prev.map(o => o.order_no === orderNo
        ? { ...o, arrival_date: data.arrivalDate, transport_mode: data.transportMode, notes: data.notes }
        : o
      ));
      setEditing(prev => { const n = { ...prev }; delete n[orderNo]; return n; });
    } catch {}
  };

  const months = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
  const getMonth = (d) => d ? months[new Date(d).getMonth()] : '';

  const exportOrders = (withPrices) => {
    const ordersToExport = selectedOrders.size > 0
      ? sortedOrders.filter(o => selectedOrders.has(o.order_no))
      : sortedOrders;
    if (!ordersToExport.length) return notifyMessage('No orders to export', 'אין הזמנות לייצוא');
    const rows = [];
    for (const o of ordersToExport) {
      for (const item of (o.items || [])) {
        const row = {
          'Order No': o.order_no,
          'Doc Type': o.doc_type,
          'Origin': o.origin,
          'Transport': o.transport_mode || '',
          'ETA': o.arrival_date ? new Date(o.arrival_date).toLocaleDateString('en-GB') : '',
          'SKU': item.sku,
          'Barcode': item.barcode || '',
          'Product': item.description,
          'Quantity': item.quantity,
        };
        if (withPrices) {
          row['Unit Price'] = item.unitPrice || 0;
          row['Amount'] = item.amount || 0;
        }
        rows.push(row);
      }
      // Order total row
      const totalRow = { 'Order No': '', 'Doc Type': '', 'Origin': '', 'Transport': '', 'ETA': '', 'SKU': '', 'Barcode': '', 'Product': `TOTAL ${o.order_no}`, 'Quantity': o.total_units };
      if (withPrices) { totalRow['Unit Price'] = ''; totalRow['Amount'] = o.total_amount; }
      rows.push(totalRow);
      rows.push({}); // empty row between orders
    }
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = withPrices
      ? [{wch:12},{wch:6},{wch:6},{wch:8},{wch:12},{wch:14},{wch:35},{wch:10},{wch:10},{wch:12}]
      : [{wch:12},{wch:6},{wch:6},{wch:8},{wch:12},{wch:14},{wch:35},{wch:10}];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Logistics');
    XLSX.writeFile(wb, `logistics_${withPrices ? 'with_prices' : 'no_prices'}.xlsx`);
  };

  const toggleSelectOrder = (orderNo) => {
    setSelectedOrders(prev => {
      const next = new Set(prev);
      next.has(orderNo) ? next.delete(orderNo) : next.add(orderNo);
      return next;
    });
  };

  const toggleSelectLine = (orderNo, idx) => {
    const key = `${orderNo}::${idx}`;
    setSelectedLines(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const toggleSelectAllLinesInOrder = (order) => {
    setSelectedLines(prev => {
      const next = new Set(prev);
      const allKeys = (order.items || []).map((_, i) => `${order.order_no}::${i}`);
      const allSelected = allKeys.length > 0 && allKeys.every(k => next.has(k));
      if (allSelected) allKeys.forEach(k => next.delete(k));
      else allKeys.forEach(k => next.add(k));
      return next;
    });
  };

  const exportSelectedLines = () => {
    const rows = [];
    let totalQty = 0, totalVal = 0, totalCBM = 0, totalWt = 0;
    for (const o of sortedOrders) {
      for (let i = 0; i < (o.items || []).length; i++) {
        if (!selectedLines.has(`${o.order_no}::${i}`)) continue;
        const it = o.items[i];
        const qty = parseFloat(it.quantity) || 0;
        const cbm = qty * (it.cbmPerUnit || 0);
        const wt = qty * (it.weightKg || 0);
        totalQty += qty; totalVal += it.amount || 0; totalCBM += cbm; totalWt += wt;
        rows.push({
          'Order No': o.order_no,
          'Doc Type': o.doc_type,
          'Origin': o.origin,
          'Transport': o.transport_mode || '',
          'ETA': o.arrival_date ? new Date(o.arrival_date).toLocaleDateString('en-GB') : '',
          'SKU': it.sku || it.item_no,
          'Barcode': it.barcode || '',
          'Product': it.description,
          'Quantity': qty,
          'Unit Price': it.unitPrice || 0,
          'Amount': it.amount || 0,
          'CBM/unit': it.cbmPerUnit || 0,
          'Total CBM': Math.round(cbm * 100) / 100,
          'Weight/unit (kg)': it.weightKg || 0,
          'Total Weight (kg)': Math.round(wt * 100) / 100,
        });
      }
    }
    if (!rows.length) return notifyMessage('No lines selected', 'לא נבחרו שורות');
    const pallets = Math.ceil(totalCBM / PALLET_CBM);
    const containers = Math.ceil(totalCBM / CONTAINER_40HC_CBM);
    rows.push({});
    rows.push({
      'Order No': 'TOTAL',
      'SKU': `${rows.length - 2} lines`,
      'Quantity': totalQty,
      'Amount': Math.round(totalVal * 100) / 100,
      'Total CBM': Math.round(totalCBM * 100) / 100,
      'Total Weight (kg)': Math.round(totalWt * 100) / 100,
    });
    rows.push({ 'Order No': '', 'Product': `Pallets: ${pallets} · 40HC: ${containers}` });
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [{wch:12},{wch:6},{wch:6},{wch:8},{wch:12},{wch:14},{wch:14},{wch:35},{wch:10},{wch:10},{wch:12},{wch:9},{wch:10},{wch:12},{wch:14}];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Selected Lines');
    XLSX.writeFile(wb, `logistics_lines_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  if (loading) return <div className="loading"><div className="spinner" />{t('common.loading')}</div>;

  // Sort and filter orders
  const sortedOrders = (() => {
    let filtered = orders.filter(o => {
      // Tab filter
      if (logTab === 'inStock' && !o.received) return false;
      if (logTab === 'shipped' && (o.doc_type !== 'SI' || o.received)) return false;
      if (logTab === 'pending' && o.doc_type !== 'SO') return false;
      // Additional filters
      if (filterOrigin && o.origin !== filterOrigin) return false;
      if (filterType && o.doc_type !== filterType) return false;
      return true;
    });
    return [...filtered].sort((a, b) => {
      let va, vb;
      switch (sortBy) {
        case 'arrival':
          va = a.arrival_date ? new Date(a.arrival_date).getTime() : 9999999999999;
          vb = b.arrival_date ? new Date(b.arrival_date).getTime() : 9999999999999;
          break;
        case 'units': va = a.total_units || 0; vb = b.total_units || 0; break;
        case 'value': va = a.total_amount || 0; vb = b.total_amount || 0; break;
        case 'origin': va = a.origin || ''; vb = b.origin || ''; break;
        default: va = 0; vb = 0;
      }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  })();

  // Product search: collect all items, filter, bucket by payment due
  const searchLower = search.trim().toLowerCase();
  const searchResults = (() => {
    if (!searchLower) return null;
    const buckets = { within30: 0, between30_60: 0, over90: 0, noDate: 0, items: [] };
    for (const order of orders) {
      if (!order.items) continue;
      for (const item of order.items) {
        const text = `${item.sku || ''} ${item.displaySku || ''} ${item.description || ''}`.toLowerCase();
        const words = searchLower.split(/\s+/).filter(Boolean);
        if (!words.every(w => text.includes(w))) continue;
        const arrDate = order.arrival_date;
        const days = arrDate ? daysUntil(new Date(arrDate)) : null;
        const qty = item.quantity || 0;
        if (days !== null) {
          if (days <= 30) buckets.within30 += qty;
          else if (days <= 60) buckets.between30_60 += qty;
          else buckets.over90 += qty;
        } else {
          buckets.noDate += qty;
        }
        buckets.items.push({
          ...item,
          orderNo: order.order_no,
          origin: order.origin,
          orderDate: order.order_date,
          arrivalDate: arrDate,
          daysLeft: days,
        });
      }
    }
    return buckets;
  })();

  return (
    <div>
      <div className="flex-between" style={{ marginBottom: 16 }}>
        <h2 style={{ fontFamily: 'var(--font-mono, monospace)', fontWeight: 700, letterSpacing: 1 }}>
          {t('logistics.title').toUpperCase()}
        </h2>
      </div>

      {/* Tabs */}
      <div className="sub-tabs" style={{ marginBottom: 16 }}>
        {[
          { key: 'all', label: `${t('logistics.all')} (${orders.length})` },
          { key: 'inStock', label: `${t('logistics.inStock')} (${orders.filter(o => o.received).length})` },
          { key: 'shipped', label: `${t('logistics.shipped')} (${orders.filter(o => o.doc_type === 'SI' && !o.received).length})` },
          { key: 'pending', label: `${t('logistics.pending')} (${orders.filter(o => o.doc_type === 'SO').length})` },
          { key: 'qr', label: '🔲 QR Serials' },
        ].map(tab => (
          <button key={tab.key} className={`sub-tab${logTab === tab.key ? ' active' : ''}`} onClick={() => setLogTab(tab.key)}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ═══ QR SERIALS TAB ═══ */}
      {logTab === 'qr' && <QRSerialsView canEdit={canEdit} t={t} />}

      {/* External Warehouse + Pull Orders moved to Warehouse page (2026-05-04) */}

      {/* Original Logistics views — hidden on QR tab */}
      {logTab !== 'qr' && (<>
      {/* Product Search */}
      <div style={{ marginBottom: 20 }}>
        <input
          className="input"
          placeholder={t('logistics.search')}
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ maxWidth: 400 }}
        />
      </div>

      {/* Search Results */}
      {searchResults && (
        <div style={{ marginBottom: 28 }}>
          <div className="summary-cards" style={{ gridTemplateColumns: searchResults.noDate > 0 ? 'repeat(4, 1fr)' : 'repeat(3, 1fr)' }}>
            <div className="summary-card success">
              <div className="label">{t('logistics.arrives30')}</div>
              <div className="value">{fmtQty(searchResults.within30)}</div>
              <div className="sub">{t('common.units')}</div>
            </div>
            <div className="summary-card warning">
              <div className="label">{t('logistics.arrives60')}</div>
              <div className="value">{fmtQty(searchResults.between30_60)}</div>
              <div className="sub">{t('common.units')}</div>
            </div>
            <div className="summary-card danger">
              <div className="label">{t('logistics.arrives90')}</div>
              <div className="value">{fmtQty(searchResults.over90)}</div>
              <div className="sub">{t('common.units')}</div>
            </div>
            {searchResults.noDate > 0 && (
              <div className="summary-card">
                <div className="label">{t('logistics.noArrival')}</div>
                <div className="value">{fmtQty(searchResults.noDate)}</div>
                <div className="sub">{t('common.units')}</div>
              </div>
            )}
          </div>

          {searchResults.items.length > 0 && (
            <div className="table-wrap" style={{ marginTop: 12 }}>
              <table>
                <thead>
                  <tr>
                    <th>{t('portal.order')}</th>
                    <th>{t('common.sku')}</th>
                    <th>{t('common.product')}</th>
                    <th>{t('logistics.origin')}</th>
                    <th className="text-end">{t('warehouse.qty')}</th>
                    <th className="text-end">{t('profitability.sellingPrice')}</th>
                    <th className="text-end">{t('common.total')}</th>
                    <th className="text-end">{t('logistics.arrivalDate')}</th>
                  </tr>
                </thead>
                <tbody>
                  {searchResults.items.map((item, i) => (
                    <tr key={i}>
                      <td className="fw-bold" style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 12 }}>{item.orderNo}</td>
                      <td style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 12, color: '#5c6370' }}>{item.sku}{item.barcode && item.barcode !== item.sku && <div style={{ fontSize: 10, color: '#999' }}>{item.barcode}</div>}</td>
                      <td>{item.description}</td>
                      <td><span className={`badge badge-${(item.origin || '').toLowerCase()}`}>{item.origin}</span></td>
                      <td className="text-end fw-bold">{fmtQty(item.quantity)}</td>
                      <td className="text-end">{item.unitPrice ? item.unitPrice.toFixed(2) : '--'}</td>
                      <td className="text-end fw-bold">{fmt(item.amount)}</td>
                      <td className="text-end">
                        {item.arrivalDate ? (
                          <span className="badge" style={{
                            background: item.daysLeft <= 0 ? '#d4edda' : item.daysLeft <= 30 ? '#fff3cd' : '#f8f9fa',
                            color: item.daysLeft <= 0 ? '#155724' : item.daysLeft <= 30 ? '#856404' : '#5c6370',
                            fontSize: 11, whiteSpace: 'nowrap'
                          }}>
                            📦 {fmtDate(item.arrivalDate)} · {item.daysLeft}d
                          </span>
                        ) : <span className="text-muted">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Sort & Filter Controls */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <select className="input input-sm" style={{ width: 140 }} value={filterOrigin} onChange={e => setFilterOrigin(e.target.value)}>
          <option value="">{t('orders.all')} {t('logistics.origin')}</option>
          <option value="CN">🇨🇳 CN</option>
          <option value="EU">🇪🇺 EU</option>
        </select>
        <select className="input input-sm" style={{ width: 140 }} value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="">{t('orders.all')}</option>
          <option value="SI">SI ({t('orders.shipped')})</option>
          <option value="SO">SO ({t('orders.pending')})</option>
        </select>
        <div style={{ display: 'flex', gap: 4 }}>
          <button className="btn btn-sm btn-gold" onClick={() => exportOrders(true)}>
            ⬇️ {selectedOrders.size > 0 ? `${t('logistics.exportN')} ${selectedOrders.size}` : t('logistics.exportAll')} ({t('logistics.exportPrices')})
          </button>
          <button className="btn btn-sm" style={{ background: '#2e7d32', color: '#fff', border: 'none' }} onClick={() => exportOrders(false)}>
            ⬇️ {selectedOrders.size > 0 ? `${t('logistics.exportN')} ${selectedOrders.size}` : t('logistics.exportAll')} ({t('logistics.exportNoPrices')})
          </button>
          {selectedOrders.size > 0 && (
            <button className="btn btn-sm btn-outline" onClick={() => setSelectedOrders(new Set())} style={{ color: 'var(--red)' }}>✕ {t('common.cancel')}</button>
          )}
        </div>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 11, color: '#6c757d', textTransform: 'uppercase' }}>{t('common.sort')}:</span>
        {[
          ['arrival', t('logistics.arrivalDate')],
          ['units', t('warehouse.qty')],
          ['value', t('common.value')],
          ['origin', t('logistics.origin')],
        ].map(([key, label]) => (
          <button key={key}
            className={`btn btn-sm ${sortBy === key ? 'btn-gold' : 'btn-outline'}`}
            style={{ fontSize: 11 }}
            onClick={() => toggleSort(key)}>
            {label} {sortBy === key ? (sortDir === 'asc' ? '▲' : '▼') : ''}
          </button>
        ))}
      </div>

      {/* Line-level selection summary — shown when at least one line is selected */}
      {selectedLines.size > 0 && (() => {
        let units = 0, value = 0, cbm = 0, weight = 0;
        for (const o of sortedOrders) {
          for (let i = 0; i < (o.items || []).length; i++) {
            if (!selectedLines.has(`${o.order_no}::${i}`)) continue;
            const it = o.items[i];
            const qty = parseFloat(it.quantity) || 0;
            units += qty; value += it.amount || 0;
            cbm += qty * (it.cbmPerUnit || 0);
            weight += qty * (it.weightKg || 0);
          }
        }
        const pallets = Math.ceil(cbm / PALLET_CBM);
        const containers = Math.ceil(cbm / CONTAINER_40HC_CBM);
        const he = document.documentElement.dir === 'rtl';
        return (
          <div style={{ position: 'sticky', top: 10, zIndex: 10, marginBottom: 16, padding: 14, background: 'linear-gradient(135deg,#dbeafe,#bfdbfe)', borderRadius: 10, border: '1px solid #3b82f6', boxShadow: '0 4px 12px rgba(59,130,246,0.2)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#1e3a8a' }}>
                ✓ {selectedLines.size} {he ? 'שורות נבחרו' : 'lines selected'}
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 10, textTransform: 'uppercase', color: '#1e3a8a' }}>{t('warehouse.qty')}</div>
                <div className="fw-bold" style={{ fontSize: 16 }}>{fmtQty(units)}</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 10, textTransform: 'uppercase', color: '#1e3a8a' }}>{t('common.value')}</div>
                <div className="fw-bold" style={{ fontSize: 16 }}>{fmt(value)}</div>
              </div>
              {cbm > 0 && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 10, textTransform: 'uppercase', color: '#1e3a8a' }}>CBM</div>
                  <div className="fw-bold" style={{ fontSize: 16, color: '#1565c0' }}>{cbm.toFixed(1)}</div>
                </div>
              )}
              {weight > 0 && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 10, textTransform: 'uppercase', color: '#1e3a8a' }}>{t('logistics.weight')}</div>
                  <div className="fw-bold" style={{ fontSize: 16, color: '#1565c0' }}>{fmtWeight(weight)}</div>
                </div>
              )}
              {cbm > 0 && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 10, textTransform: 'uppercase', color: '#1e3a8a' }}>{he ? 'פלטות' : 'Pallets'}</div>
                  <div className="fw-bold" style={{ fontSize: 16, color: '#0d47a1' }}>{pallets}</div>
                </div>
              )}
              {cbm > 0 && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 10, textTransform: 'uppercase', color: '#1e3a8a' }}>40HC</div>
                  <div className="fw-bold" style={{ fontSize: 16, color: '#0d47a1' }}>{containers} × 🚛</div>
                </div>
              )}
              <div style={{ flex: 1 }} />
              <button className="btn btn-sm btn-gold" onClick={exportSelectedLines}>
                ⬇️ {he ? 'ייצוא לאקסל' : 'Export to Excel'}
              </button>
              <button className="btn btn-sm btn-outline" onClick={() => setSelectedLines(new Set())} style={{ color: 'var(--red)' }}>
                ✕ {t('common.cancel')}
              </button>
            </div>
          </div>
        );
      })()}

      {/* Order-level selection summary — shown when whole orders are checked */}
      {selectedOrders.size > 0 && (() => {
        const selected = sortedOrders.filter(o => selectedOrders.has(o.order_no));
        const sumUnits = selected.reduce((s, o) => s + (o.total_units || 0), 0);
        const sumValue = selected.reduce((s, o) => s + (o.total_amount || 0), 0);
        const sumCBM = selected.reduce((s, o) => s + (o.items || []).reduce((s2, i) => s2 + (i.quantity || 0) * (i.cbmPerUnit || 0), 0), 0);
        const sumWeight = selected.reduce((s, o) => s + (o.items || []).reduce((s2, i) => s2 + (i.quantity || 0) * (i.weightKg || 0), 0), 0);
        const combinedPallets = Math.ceil(sumCBM / PALLET_CBM);
        const combinedContainers = Math.ceil(sumCBM / CONTAINER_40HC_CBM);
        const separateContainers = selected.reduce((s, o) => {
          const cbm = (o.items || []).reduce((s2, i) => s2 + (i.quantity || 0) * (i.cbmPerUnit || 0), 0);
          return s + (cbm > 0 ? Math.ceil(cbm / CONTAINER_40HC_CBM) : 0);
        }, 0);
        const containerSavings = separateContainers - combinedContainers;
        const he = document.documentElement.dir === 'rtl';

        return (
          <div style={{ marginBottom: 16, padding: 14, background: 'linear-gradient(135deg,#fef3c7,#fde68a)', borderRadius: 10, border: '1px solid #f59e0b' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#78350f' }}>
                ✓ {selectedOrders.size} {he ? 'הזמנות נבחרו' : 'orders selected'}
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 10, textTransform: 'uppercase', color: '#78350f' }}>{t('warehouse.qty')}</div>
                <div className="fw-bold" style={{ fontSize: 16 }}>{fmtQty(sumUnits)}</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 10, textTransform: 'uppercase', color: '#78350f' }}>{t('common.value')}</div>
                <div className="fw-bold" style={{ fontSize: 16 }}>{fmt(sumValue)}</div>
              </div>
              {sumCBM > 0 && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 10, textTransform: 'uppercase', color: '#78350f' }}>CBM</div>
                  <div className="fw-bold" style={{ fontSize: 16, color: '#1565c0' }}>{sumCBM.toFixed(1)}</div>
                </div>
              )}
              {sumWeight > 0 && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 10, textTransform: 'uppercase', color: '#78350f' }}>{t('logistics.weight')}</div>
                  <div className="fw-bold" style={{ fontSize: 16, color: '#1565c0' }}>{fmtWeight(sumWeight)}</div>
                </div>
              )}
              {sumCBM > 0 && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 10, textTransform: 'uppercase', color: '#78350f' }}>{he ? 'פלטות' : 'Pallets'}</div>
                  <div className="fw-bold" style={{ fontSize: 16, color: '#0d47a1' }}>{combinedPallets}</div>
                </div>
              )}
              {sumCBM > 0 && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 10, textTransform: 'uppercase', color: '#78350f' }}>{he ? 'קונטיינרים (מאוחד)' : '40HC (combined)'}</div>
                  <div className="fw-bold" style={{ fontSize: 16, color: '#0d47a1' }}>{combinedContainers} × 🚛</div>
                </div>
              )}
              {containerSavings > 0 && (
                <div style={{ textAlign: 'center', padding: '4px 10px', background: '#dcfce7', borderRadius: 6, border: '1px solid #86efac' }}>
                  <div style={{ fontSize: 10, textTransform: 'uppercase', color: '#166534' }}>{he ? 'חיסכון' : 'Savings'}</div>
                  <div className="fw-bold" style={{ fontSize: 13, color: '#166534' }}>
                    {separateContainers} → {combinedContainers} ({he ? 'חיסכון של' : 'save'} {containerSavings})
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {sortedOrders.length === 0 ? (
        <div className="empty-state">{t('common.noData')}</div>
      ) : sortedOrders.map((order, idx) => {
        const isEditing = !!editing[order.order_no];
        const arrivalDate = getField(order, 'arrivalDate');
        const transportMode = getField(order, 'transportMode');
        const skuCount = order.items?.length || 0;
        const totalUnits = order.total_units || 0;
        const totalAmount = order.total_amount || 0;
        const isShipped = order.doc_type === 'SI';
        const totalCBM = (order.items || []).reduce((s, i) => s + (i.quantity || 0) * (i.cbmPerUnit || 0), 0);
        const totalWeightKg = (order.items || []).reduce((s, i) => s + (i.quantity || 0) * (i.weightKg || 0), 0);
        const totalPallets = Math.ceil(totalCBM / PALLET_CBM);
        const totalContainers = Math.ceil(totalCBM / CONTAINER_40HC_CBM);

        return (
          <div key={`${order.order_no}-${idx}`} className="order-group" style={{ marginBottom: 16 }}>
            {/* Collapsed Header */}
            <div className="order-group-header" onClick={() => toggle(order.order_no)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px' }}>
              <input type="checkbox" checked={selectedOrders.has(order.order_no)} onChange={(e) => { e.stopPropagation(); toggleSelectOrder(order.order_no); }} onClick={e => e.stopPropagation()} style={{ marginInlineEnd: 4 }} />
              <span className={`arrow${expanded.has(order.order_no) ? ' open' : ''}`}>&#9654;</span>
              <span className="order-no fw-bold">{order.order_no}</span>
              <span className="text-muted" style={{ fontSize: 12 }}>{getMonth(order.order_date)}</span>
              {isShipped && (
                <span className="badge" style={{ background: '#d4edda', color: '#155724', fontSize: 11 }}>
                  ✓ {t('portal.shipped')}
                </span>
              )}
              {!isShipped && (
                <span className="badge" style={{ background: '#fff3cd', color: '#856404', fontSize: 11 }}>
                  {t('warehouse.pending')}
                </span>
              )}
              {order.received && !order.received_no_stock && <span className="badge" style={{ background: '#4caf50', color: '#fff', fontSize: 11 }}>✓ {t('logistics.inStock')}</span>}
              {order.received_no_stock && <span className="badge" style={{ background: '#1565c0', color: '#fff', fontSize: 11 }}>✓ {i18n.language === 'he' ? 'כבר בספירה' : 'already counted'}</span>}
              {order.supplier_added && <span className="badge" style={{ background: '#6a1b9a', color: '#fff', fontSize: 11 }}>✓ {i18n.language === 'he' ? 'במלאי ספק' : 'In Supplier Stock'}</span>}
              {canEdit && !order.received && (<>
                <button className="btn btn-sm" style={{ marginInlineStart: 4, background: '#4caf50', color: '#fff', border: 'none', fontSize: 10, padding: '2px 8px' }}
                  onClick={async (e) => { e.stopPropagation(); if (!(await confirm({ body: i18n.language === 'he' ? `לקבל את ${order.order_no} למלאי שאגר?` : `Receive ${order.order_no} into Shager Stock?` }))) return; try { await api.post('/warehouse/receive', { orderNo: order.order_no }); window.location.reload(); } catch (err) { notifyError(err); } }}>
                  {t('warehouse.receiveToStock')}
                </button>
                <button className="btn btn-sm" style={{ marginInlineStart: 4, background: '#1565c0', color: '#fff', border: 'none', fontSize: 10, padding: '2px 8px' }}
                  onClick={async (e) => { e.stopPropagation(); if (!(await confirm({ body: i18n.language === 'he' ? `סמן את ${order.order_no} כהתקבל מבלי להוסיף למלאי? (המלאי כבר נספר ידנית)` : `Mark ${order.order_no} received WITHOUT adding to stock? (stock already counted manually)` }))) return; try { await api.post('/warehouse/receive-no-stock', { orderNo: order.order_no }); window.location.reload(); } catch (err) { notifyError(err); } }}>
                  {i18n.language === 'he' ? '✓ כבר בספירה' : '✓ Already Counted'}
                </button>
              </>)}
              {canEdit && order.received && !order.received_no_stock && (
                <button className="btn btn-sm" style={{ marginInlineStart: 4, background: '#c62828', color: '#fff', border: 'none', fontSize: 10, padding: '2px 6px' }}
                  onClick={async (e) => { e.stopPropagation(); if (!(await confirm({ body: i18n.language === 'he' ? `להסיר את ${order.order_no} ממלאי שאגר?` : `Remove ${order.order_no} from Shager Stock?`, danger: true }))) return; try { await api.delete(`/warehouse/receive/${order.order_no}`); window.location.reload(); } catch (err) { notifyError(err); } }}>
                  ✕
                </button>
              )}
              {canEdit && order.received_no_stock && (
                <button className="btn btn-sm" style={{ marginInlineStart: 4, background: '#c62828', color: '#fff', border: 'none', fontSize: 10, padding: '2px 6px' }}
                  onClick={async (e) => { e.stopPropagation(); if (!(await confirm({ body: i18n.language === 'he' ? `בטל סימון "כבר בספירה" עבור ${order.order_no}?` : `Undo "already counted" for ${order.order_no}?` }))) return; try { await api.delete(`/warehouse/receive-no-stock/${order.order_no}`); window.location.reload(); } catch (err) { notifyError(err); } }}>
                  ✕
                </button>
              )}
              {canEdit && !order.supplier_added && (
                <button className="btn btn-sm" style={{ marginInlineStart: 4, background: '#6a1b9a', color: '#fff', border: 'none', fontSize: 10, padding: '2px 8px' }}
                  onClick={async (e) => { e.stopPropagation(); if (!(await confirm({ body: i18n.language === 'he' ? `להוסיף את ${order.order_no} למלאי הספק (רעיוני)?` : `Add ${order.order_no} to supplier stock?` }))) return; try { await api.post(`/supplier/order-stock/${order.order_no}`); window.location.reload(); } catch (err) { notifyError(err); } }}>
                  {i18n.language === 'he' ? '➕ למלאי ספק' : '➕ To Supplier Stock'}
                </button>
              )}
              {canEdit && order.supplier_added && (
                <button className="btn btn-sm" style={{ marginInlineStart: 4, background: '#6a1b9a', color: '#fff', border: 'none', fontSize: 10, padding: '2px 6px' }}
                  onClick={async (e) => { e.stopPropagation(); if (!(await confirm({ body: i18n.language === 'he' ? `להסיר את ${order.order_no} ממלאי הספק?` : `Remove ${order.order_no} from supplier stock?`, danger: true }))) return; try { await api.delete(`/supplier/order-stock/${order.order_no}`); window.location.reload(); } catch (err) { notifyError(err); } }}>
                  ✕ {i18n.language === 'he' ? 'ספק' : 'Supplier'}
                </button>
              )}
              <div style={{ flex: 1 }} />
              <div style={{ display: 'flex', gap: 16, alignItems: 'center', fontSize: 12, color: '#6c757d' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 10, textTransform: 'uppercase' }}>{t('logistics.arrivalDate')}</div>
                  <div className="fw-bold" style={{ color: '#333' }}>{arrivalDate ? fmtDate(arrivalDate) : '—'}</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 10, textTransform: 'uppercase' }}>{t('logistics.transport')}</div>
                  {transportMode ? (
                    <span className="badge" style={{
                      background: transportMode === 'AIR' ? '#e3f2fd' : '#e8f5e9',
                      color: transportMode === 'AIR' ? '#1565c0' : '#2e7d32',
                      fontSize: 11
                    }}>
                      {transportMode === 'AIR' ? '✈ Air' : '🚢 Ocean'}
                    </span>
                  ) : <span>—</span>}
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 10, textTransform: 'uppercase' }}>{t('logistics.origin')}</div>
                  <span className={`badge badge-${(order.origin || '').toLowerCase()}`}>{order.origin}</span>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 10, textTransform: 'uppercase' }}>{t('warehouse.qty')}</div>
                  <div className="fw-bold" style={{ color: '#333' }}>{fmtQty(totalUnits)}</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 10, textTransform: 'uppercase' }}>{t('logistics.totalValue')}</div>
                  <div className="fw-bold" style={{ color: '#333' }}>{fmt(totalAmount)}</div>
                </div>
                {totalCBM > 0 && (
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 10, textTransform: 'uppercase' }}>CBM</div>
                    <div className="fw-bold" style={{ color: '#1565c0' }}>{totalCBM.toFixed(1)}</div>
                  </div>
                )}
                {totalWeightKg > 0 && (
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 10, textTransform: 'uppercase' }}>{t('logistics.weight')}</div>
                    <div className="fw-bold" style={{ color: '#1565c0' }}>{fmtWeight(totalWeightKg)}</div>
                  </div>
                )}
                {totalCBM > 0 && (
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 10, textTransform: 'uppercase' }}>40HC</div>
                    <div className="fw-bold" style={{ color: '#0d47a1' }}>{totalContainers} × 🚛</div>
                  </div>
                )}
              </div>
            </div>

            {/* Expanded Body */}
            {expanded.has(order.order_no) && (
              <div className="order-group-body" style={{ padding: 16 }}>
                {/* Editable Top Section */}
                <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', marginBottom: 16, padding: 16, background: '#f8f9fa', borderRadius: 8 }}>
                  <div>
                    <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#6c757d', marginBottom: 4 }}>{t('logistics.arrivalDate')}</div>
                    {canEdit ? (
                      <input type="date" className="input input-sm" style={{ width: 160 }}
                        value={arrivalDate ? (typeof arrivalDate === 'string' ? arrivalDate.slice(0, 10) : new Date(arrivalDate).toISOString().slice(0, 10)) : ''}
                        onChange={e => updateField(order.order_no, 'arrivalDate', e.target.value)} />
                    ) : (
                      <span className="fw-bold">{arrivalDate ? fmtDate(arrivalDate) : '—'}</span>
                    )}
                  </div>
                  <div>
                    <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#6c757d', marginBottom: 4 }}>{t('logistics.transportMode')}</div>
                    {canEdit ? (
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className={`btn btn-sm ${transportMode === 'AIR' ? 'btn-gold' : 'btn-outline'}`}
                          style={{ fontSize: 12 }}
                          onClick={() => updateField(order.order_no, 'transportMode', 'AIR')}>
                          ✈ Air
                        </button>
                        <button className={`btn btn-sm ${transportMode === 'SEA' ? 'btn-gold' : 'btn-outline'}`}
                          style={{ fontSize: 12 }}
                          onClick={() => updateField(order.order_no, 'transportMode', 'SEA')}>
                          🚢 Ocean
                        </button>
                      </div>
                    ) : (
                      <span className="fw-bold">{transportMode || '—'}</span>
                    )}
                  </div>
                  <div style={{ flex: 1 }} />
                  <div style={{ textAlign: 'end' }}>
                    <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#6c757d' }}>{t('orders.totalUnits')}</div>
                    <div style={{ fontSize: 28, fontWeight: 700, fontFamily: 'var(--font-mono, monospace)' }}>{fmtQty(totalUnits)}</div>
                    <div style={{ fontSize: 12, color: '#6c757d' }}>{skuCount} {t('logistics.skuCount')}</div>
                  </div>
                  {canEdit && isEditing && (
                    <div style={{ alignSelf: 'flex-end' }}>
                      <button className="btn btn-gold btn-sm" onClick={() => saveEdit(order.order_no, order)}>
                        {t('common.save')}
                      </button>
                    </div>
                  )}
                </div>

                {/* Order Info Row */}
                <div style={{ display: 'flex', gap: 32, marginBottom: 16, fontSize: 13 }}>
                  <div>
                    <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#6c757d' }}>{t('logistics.origin')}</div>
                    <div className="fw-bold">
                      {order.origin === 'CN' ? '🇨🇳 China' : '🇪🇺 Europe'}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#6c757d' }}>{t('logistics.orderDate')}</div>
                    <div className="fw-bold">{fmtDate(order.order_date)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#6c757d' }}>{t('logistics.transport')}</div>
                    <div className="fw-bold">{isShipped ? t('portal.shipped') : t('warehouse.pending')}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#6c757d' }}>{t('logistics.wave')}</div>
                    <div className="fw-bold">{getMonth(order.order_date)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#6c757d' }}>{t('logistics.totalValue')}</div>
                    <div className="fw-bold">{fmt(totalAmount)}</div>
                  </div>
                </div>

                {/* Line Items Table */}
                {order.items && order.items.length > 0 && (
                  <div className="table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th style={{ width: 32 }}>
                            <input
                              type="checkbox"
                              checked={order.items.length > 0 && order.items.every((_, i) => selectedLines.has(`${order.order_no}::${i}`))}
                              onChange={() => toggleSelectAllLinesInOrder(order)}
                              title="Select all lines"
                            />
                          </th>
                          <th>{t('common.sku')}</th>
                          <th>{t('common.product')}</th>
                          <th className="text-end">{t('warehouse.qty')}</th>
                          <th className="text-end">{t('profitability.sellingPrice')}</th>
                          <th className="text-end">{t('common.total')}</th>
                          <th className="text-end">CBM</th>
                          <th className="text-end">{t('logistics.weight')}</th>
                          <th className="text-end">{t('logistics.eta')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {order.items.map((item, i) => {
                          const arrDays = order.arrival_date ? daysUntil(new Date(order.arrival_date)) : null;
                          const lineKey = `${order.order_no}::${i}`;
                          const isSelected = selectedLines.has(lineKey);
                          return (
                            <tr key={i} style={isSelected ? { background: '#dbeafe' } : undefined}>
                              <td>
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleSelectLine(order.order_no, i)}
                                />
                              </td>
                              <td>{item.sku || item.item_no}{item.barcode && item.barcode !== item.sku && <div className="text-muted" style={{ fontSize: 10 }}>{item.barcode}</div>}</td>
                              <td>{item.description}</td>
                              <td className="text-end fw-bold">{fmtQty(item.quantity)}</td>
                              <td className="text-end">{item.unitPrice ? item.unitPrice.toFixed(2) : '--'}</td>
                              <td className="text-end fw-bold">{fmt(item.amount)}</td>
                              <td className="text-end" style={{ color: '#6c757d' }}>
                                {(item.quantity * (item.cbmPerUnit || 0)) > 0 ? (item.quantity * item.cbmPerUnit).toFixed(2) : '—'}
                              </td>
                              <td className="text-end" style={{ color: '#6c757d' }}>
                                {(item.quantity * (item.weightKg || 0)) > 0 ? fmtWeight(item.quantity * item.weightKg) : '—'}
                              </td>
                              <td className="text-end">
                                {order.arrival_date ? (
                                  <span className="badge" style={{
                                    background: arrDays <= 0 ? '#d4edda' : arrDays <= 30 ? '#fff3cd' : '#f8f9fa',
                                    color: arrDays <= 0 ? '#155724' : arrDays <= 30 ? '#856404' : '#5c6370',
                                    fontSize: 11, whiteSpace: 'nowrap'
                                  }}>
                                    {arrDays <= 0 ? '✓' : `${arrDays}d`}
                                  </span>
                                ) : '—'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr style={{ borderTop: '2px solid #dee2e6' }}>
                          <td />
                          <td colSpan="2">
                            <span className="fw-bold">{t('logistics.totalUnits')}: {fmtQty(totalUnits)}</span>
                            <span className="text-muted" style={{ marginInlineStart: 12 }}>{t('logistics.skuCount')}: {skuCount}</span>
                          </td>
                          <td colSpan="2" />
                          <td className="text-end fw-bold">{fmt(totalAmount)}</td>
                          <td className="text-end fw-bold" style={{ color: '#1565c0' }}>{totalCBM.toFixed(2)}</td>
                          <td className="text-end fw-bold" style={{ color: '#1565c0' }}>{totalWeightKg > 0 ? fmtWeight(totalWeightKg) : ''}</td>
                          <td />
                        </tr>
                        {totalCBM > 0 && (() => {
                          const containersWithPallets = Math.ceil(totalPallets / PALLETS_PER_40HC);
                          const containersLoose = totalContainers;
                          const palletCBM = totalPallets * PALLET_CBM;
                          const fillPallets = Math.round((palletCBM / (containersWithPallets * CONTAINER_40HC_CBM)) * 100);
                          const fillLoose = Math.round((totalCBM / (containersLoose * CONTAINER_40HC_CBM)) * 100);
                          return (<>
                          <tr style={{ background: '#e3f2fd' }}>
                            <td colSpan="4" style={{ fontWeight: 600, color: '#0d47a1' }}>📦 {t('logistics.pallets')}</td>
                            <td className="text-end" style={{ fontWeight: 700, color: '#0d47a1' }}>{containersWithPallets} × 40HC</td>
                            <td className="text-end" style={{ fontWeight: 700, color: '#0d47a1' }}>
                              {totalPallets} {t('logistics.pallets')} · {palletCBM.toFixed(1)} CBM
                            </td>
                            <td className="text-end" style={{ fontWeight: 700, color: '#0d47a1' }}>
                              {totalWeightKg > 0 ? fmtWeight(totalWeightKg) : ''}
                            </td>
                            <td colSpan="2" style={{ fontSize: 11, color: '#0d47a1' }}>
                              {fillPallets}% full
                            </td>
                          </tr>
                          <tr style={{ background: '#e8f5e9' }}>
                            <td colSpan="4" style={{ fontWeight: 600, color: '#2e7d32' }}>🚛 {t('logistics.containers')}</td>
                            <td className="text-end" style={{ fontWeight: 700, color: '#2e7d32' }}>{containersLoose} × 40HC</td>
                            <td className="text-end" style={{ fontWeight: 700, color: '#2e7d32' }}>
                              {totalCBM.toFixed(1)} / {(containersLoose * CONTAINER_40HC_CBM).toFixed(1)} CBM
                            </td>
                            <td className="text-end" style={{ fontWeight: 700, color: '#2e7d32' }}>
                              {totalWeightKg > 0 ? fmtWeight(totalWeightKg) : ''}
                            </td>
                            <td colSpan="2" style={{ fontSize: 11, color: '#2e7d32' }}>
                              {fillLoose}% full
                            </td>
                          </tr>
                          </>);
                        })()}
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
      </>)}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// QR Serials View — grouped by order → sku → master carton
// ═══════════════════════════════════════════════════════════════
export function QRSerialsView({ canEdit, t }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [filter, setFilter] = useState('all');
  const [handledFilter, setHandledFilter] = useState('all');  // all | handled | pending
  const [expanded, setExpanded] = useState(new Set());
  const [expandedSkus, setExpandedSkus] = useState(new Set());
  const [expandedCartons, setExpandedCartons] = useState(new Set());
  const he = document.documentElement.dir === 'rtl';

  const fetchData = () => {
    setLoading(true);
    api.get('/logistics/qr-serials').then(r => setData(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { fetchData(); }, []);

  const doSearch = async () => {
    if (!search.trim()) { setSearchResults(null); return; }
    try {
      const r = await api.get('/logistics/qr-serials/search', { params: { q: search.trim() } });
      setSearchResults(r.data);
    } catch {}
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    setUploading(true);
    try {
      const r = await api.post('/logistics/qr-serials/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const { inserted, duplicates, skipped, autoMatched, unknownSkus, unknownOrders } = r.data;
      let msg = he
        ? `${inserted} סריאלים הועלו\nהותאמו אוטומטית ל-SI: ${autoMatched}\nכפילויות: ${duplicates}\nדולגו: ${skipped}`
        : `${inserted} serials uploaded\nAuto-matched to SI: ${autoMatched}\nDuplicates: ${duplicates}\nSkipped: ${skipped}`;
      if (unknownSkus?.length) msg += `\n⚠ SKUs ${he ? 'לא מזוהים' : 'unknown'}: ${unknownSkus.join(', ')}`;
      if (unknownOrders?.length) msg += `\n⚠ ${he ? 'הזמנות ללא התאמה' : 'Orders without SI match'}: ${unknownOrders.join(', ')}`;
      alert(msg);
      fetchData();
    } catch (err) {
      notifyError(err, i18n.language === 'he' ? 'העלאה נכשלה' : 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const exportExcel = () => {
    if (!data?.orders?.length) return notifyMessage('No data to export', 'אין נתונים לייצוא');
    const rows = [];
    for (const o of data.orders) {
      for (const s of o.skus) {
        for (const c of s.cartons) {
          for (const ser of c.serials) {
            rows.push({
              'Order': o.orderNo || '',
              'Arrival': o.arrivalDate ? new Date(o.arrivalDate).toLocaleDateString('en-GB') : '',
              'Transport': o.transportMode || '',
              'Origin': o.origin || '',
              'SKU': s.sku,
              'Product': s.productName || '',
              'Status': s.status,
              'Master Carton': c.masterCarton || '',
              'Serial': ser.serial,
              'Batch Date': ser.batchDate ? new Date(ser.batchDate).toLocaleDateString('en-GB') : '',
            });
          }
        }
      }
    }
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [{wch:15},{wch:12},{wch:8},{wch:6},{wch:10},{wch:35},{wch:10},{wch:20},{wch:28},{wch:12}];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'QR Serials');
    XLSX.writeFile(wb, `qr_serials_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  if (loading) return <div className="loading"><div className="spinner" />{t('common.loading')}</div>;
  if (!data) return <div className="empty-state">Failed to load</div>;

  // Toggle single serial handled
  const toggleHandled = async (id, currentlyHandled) => {
    try {
      await api.put(`/logistics/qr-serials/${id}/handled`, { handled: !currentlyHandled });
      fetchData();
    } catch (err) { notifyError(err); }
  };

  // Bulk mark all serials of a SKU (within an order) handled/unhandled
  const bulkHandle = async (orderNo, sku, handled) => {
    try {
      await api.put('/logistics/qr-serials/bulk-handled', { scope: { orderNo, sku }, handled });
      fetchData();
    } catch (err) { notifyError(err); }
  };

  // Delete all serials of an order
  const deleteOrder = async (orderNo) => {
    if (!(await confirm({ body: he ? `למחוק את כל הסריאלים של הזמנה ${orderNo}?` : `Delete all serials of order ${orderNo}?`, danger: true }))) return;
    try {
      const r = await api.delete(`/logistics/qr-serials/order/${orderNo}`);
      alert(he ? `${r.data.deleted} סריאלים נמחקו` : `${r.data.deleted} serials deleted`);
      fetchData();
    } catch (err) { notifyError(err); }
  };

  const filteredOrders = data.orders.filter(o => {
    if (filter !== 'all' && !o.skus.some(s => s.status === filter)) return false;
    if (handledFilter === 'handled' && (o.handledSerials || 0) === 0) return false;
    if (handledFilter === 'pending' && (o.handledSerials || 0) === o.totalSerials) return false;
    return true;
  });

  return (
    <div>
      {/* Summary + actions */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <div className="summary-cards" style={{ gridTemplateColumns: 'repeat(6, 1fr)', flex: 1, minWidth: 500 }}>
          <div className="summary-card">
            <div className="label">{he ? 'סה"כ סריאלים' : 'Total Serials'}</div>
            <div className="value">{data.summary.totalSerials.toLocaleString()}</div>
          </div>
          <div className="summary-card" style={{ background: '#dcfce7' }}>
            <div className="label">✓ {he ? 'טופלו' : 'Handled'}</div>
            <div className="value" style={{ color: '#166534' }}>{(data.summary.handledCount || 0).toLocaleString()}</div>
          </div>
          <div className="summary-card" style={{ background: '#fef3c7' }}>
            <div className="label">⏳ {he ? 'לא טופלו' : 'Pending'}</div>
            <div className="value" style={{ color: '#92400e' }}>{(data.summary.pendingCount || 0).toLocaleString()}</div>
          </div>
          <div className="summary-card">
            <div className="label">{he ? 'הזמנות' : 'Orders'}</div>
            <div className="value">{data.summary.orderCount}</div>
          </div>
          <div className="summary-card">
            <div className="label">{he ? 'קרטונים' : 'Cartons'}</div>
            <div className="value">{data.summary.cartonCount}</div>
          </div>
          <div className="summary-card" style={data.summary.attentionCount > 0 ? { background: '#fee2e2', border: '1px solid #f59e0b' } : {}}>
            <div className="label">⚠ {he ? 'תשומת לב' : 'Attention'}</div>
            <div className="value" style={{ color: data.summary.attentionCount > 0 ? '#92400e' : '#16a34a' }}>{data.summary.attentionCount}</div>
          </div>
        </div>
        {canEdit && (
          <label className="btn btn-gold" style={{ cursor: 'pointer' }}>
            {uploading ? '⏳ ...' : '⬆️ ' + (he ? 'העלה Excel' : 'Upload Excel')}
            <input type="file" accept=".xlsx,.xls,.csv" onChange={onUpload} disabled={uploading} style={{ display: 'none' }} />
          </label>
        )}
        {canEdit && (
          <button className="btn btn-sm btn-outline" onClick={async () => {
            try {
              const r = await api.post('/logistics/qr-serials/rematch');
              alert(he ? `נמצאו התאמות חדשות: ${r.data.matched}` : `New matches found: ${r.data.matched}`);
              fetchData();
            } catch (err) { notifyError(err); }
          }} title={he ? 'התאם מחדש סריאלים שלא היה להם SI בזמן ההעלאה' : 'Re-match unlinked serials (after new SI arrivals)'}>
            🔄 {he ? 'התאם מחדש' : 'Re-match'}
          </button>
        )}
        <button className="btn btn-sm" style={{ background: '#2e7d32', color: '#fff', border: 'none' }} onClick={exportExcel}>⬇️ {he ? 'ייצוא' : 'Export'}</button>
      </div>

      {/* Search + filter */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <input className="input" placeholder={he ? 'חיפוש סריאל / קרטון / SKU' : 'Search serial / carton / SKU'}
          value={search} onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && doSearch()}
          style={{ maxWidth: 400 }} />
        <button className="btn btn-sm btn-outline" onClick={doSearch}>🔍</button>
        {searchResults && <button className="btn btn-sm btn-outline" onClick={() => { setSearchResults(null); setSearch(''); }}>✕ Clear</button>}
        <div style={{ flex: 1 }} />
        <span className="text-muted" style={{ fontSize: 11, textTransform: 'uppercase' }}>{he ? 'סטטוס' : 'Status'}:</span>
        {[
          { key: 'all', label: he ? 'הכל' : 'All' },
          { key: 'pending', label: he ? 'ממתין' : 'Pending' },
          { key: 'in_stock', label: he ? 'במלאי' : 'In Stock' },
          { key: 'shipped', label: he ? 'נשלח' : 'Shipped' },
        ].map(f => (
          <button key={f.key} className={`btn btn-sm ${filter === f.key ? 'btn-gold' : 'btn-outline'}`} style={{ fontSize: 11 }}
            onClick={() => setFilter(f.key)}>{f.label}</button>
        ))}
        <span className="text-muted" style={{ fontSize: 11, textTransform: 'uppercase', marginInlineStart: 12 }}>{he ? 'טיפול' : 'Handled'}:</span>
        {[
          { key: 'all', label: he ? 'הכל' : 'All' },
          { key: 'handled', label: '✓ ' + (he ? 'טופלו' : 'Handled') },
          { key: 'pending', label: '⏳ ' + (he ? 'לא טופלו' : 'Pending') },
        ].map(f => (
          <button key={f.key} className={`btn btn-sm ${handledFilter === f.key ? 'btn-gold' : 'btn-outline'}`} style={{ fontSize: 11 }}
            onClick={() => setHandledFilter(f.key)}>{f.label}</button>
        ))}
      </div>

      {/* Search results */}
      {searchResults && (
        <div className="table-wrap" style={{ marginBottom: 20 }}>
          <table>
            <thead><tr>
              <th>Serial</th><th>SKU</th><th>Product</th><th>Order</th><th>Master Carton</th><th>Arrival</th><th>Transport</th>
            </tr></thead>
            <tbody>
              {searchResults.length === 0 ? (
                <tr><td colSpan="7" className="text-muted text-center">{he ? 'לא נמצא' : 'No results'}</td></tr>
              ) : searchResults.map(r => (
                <tr key={r.id}>
                  <td style={{ fontFamily: "'DM Mono', monospace", fontSize: 11 }}>{r.serial}</td>
                  <td style={{ fontFamily: "'DM Mono', monospace", fontSize: 11 }}>{r.sku}</td>
                  <td>{r.product_name || '—'}</td>
                  <td className="text-gold">{r.order_no || '—'}</td>
                  <td style={{ fontFamily: "'DM Mono', monospace", fontSize: 11 }}>{r.master_carton || '—'}</td>
                  <td>{r.arrival_date ? new Date(r.arrival_date).toLocaleDateString('en-GB') : '—'}</td>
                  <td>{r.transport_mode || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Grouped list */}
      {filteredOrders.length === 0 ? (
        <div className="empty-state">
          {data.summary.totalSerials === 0
            ? (he ? 'אין סריאלים. העלה קובץ Excel להתחיל.' : 'No serials yet. Upload an Excel file to start.')
            : (he ? 'אין תוצאות למסנן זה' : 'No results for this filter')}
        </div>
      ) : filteredOrders.map(o => {
        const isOpen = expanded.has(o.orderNo);
        const transportIcon = o.transportMode === 'AIR' ? '✈' : o.transportMode === 'SEA' ? '🚢' : '';
        return (
          <div key={o.orderNo || 'noorder'} className="order-group" style={{ marginBottom: 10 }}>
            <div className="order-group-header" style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}
                onClick={() => setExpanded(prev => { const s = new Set(prev); s.has(o.orderNo) ? s.delete(o.orderNo) : s.add(o.orderNo); return s; })}>
                <span className={`arrow${isOpen ? ' open' : ''}`}>&#9654;</span>
                <span className="order-no fw-bold">📦 {o.orderNo || '(no order)'}</span>
                {o.supplierRefs?.length > 0 && (
                  <span className="text-muted" style={{ fontSize: 11 }} title={he ? 'מזהה ספק מקורי' : 'Supplier original ref'}>
                    ← {o.supplierRefs.join(', ')}
                  </span>
                )}
                {!o.orderNo && <span className="badge" style={{ background: '#fee2e2', color: '#991b1b', fontSize: 10 }}>{he ? 'לא מזוהה' : 'UNLINKED'}</span>}
                {o.origin && <span className={`badge badge-${o.origin.toLowerCase()}`}>{o.origin}</span>}
                {transportIcon && <span>{transportIcon} {o.transportMode}</span>}
                {o.arrivalDate && <span className="text-muted" style={{ fontSize: 12 }}>ETA {new Date(o.arrivalDate).toLocaleDateString('en-GB')}</span>}
              </span>
              <span style={{ fontSize: 12, color: '#6c757d' }}>
                <strong style={{ color: '#166534' }}>{o.handledSerials || 0}</strong> / {o.totalSerials} {he ? 'טופלו' : 'handled'}
                <span style={{ marginInlineStart: 8 }}>· {o.skus.length} SKU{o.skus.length !== 1 ? 's' : ''}</span>
              </span>
              {canEdit && o.orderNo && (
                <button className="btn btn-sm btn-danger" style={{ fontSize: 10, padding: '2px 8px', marginInlineStart: 8 }}
                  onClick={(e) => { e.stopPropagation(); deleteOrder(o.orderNo); }}
                  title={he ? 'מחק את כל הסריאלים של ההזמנה' : 'Delete all serials of this order'}>
                  🗑 {he ? 'מחק' : 'Delete'}
                </button>
              )}
            </div>
            {isOpen && (
              <div style={{ padding: 12, background: '#fafafa' }}>
                {o.skus.map(s => {
                  const skuKey = `${o.orderNo}::${s.sku}`;
                  const skuOpen = expandedSkus.has(skuKey);
                  const statusColor = s.status === 'in_stock' ? '#16a34a' : s.status === 'shipped' ? '#7c3aed' : '#f59e0b';
                  const allHandled = (s.handledSerials || 0) === s.totalSerials;
                  return (
                    <div key={s.sku} style={{ marginBottom: 8, background: '#fff', borderRadius: 6, padding: 10 }}>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                        <span style={{ cursor: 'pointer', display: 'flex', gap: 10, alignItems: 'center', flex: 1 }}
                          onClick={() => setExpandedSkus(prev => { const ns = new Set(prev); ns.has(skuKey) ? ns.delete(skuKey) : ns.add(skuKey); return ns; })}>
                          <span className={`arrow${skuOpen ? ' open' : ''}`} style={{ fontSize: 10 }}>&#9654;</span>
                          <span style={{ fontFamily: "'DM Mono', monospace", fontWeight: 700, color: '#1565c0' }}>{s.displaySku || s.sku}</span>
                          <span>{s.productName || <em style={{ color: '#dc2626' }}>⚠ Unknown product</em>}</span>
                          <span className="badge" style={{ background: statusColor + '20', color: statusColor, fontSize: 10, fontWeight: 700 }}>
                            {s.status === 'in_stock' ? (he ? 'במלאי' : 'IN STOCK') : s.status === 'shipped' ? (he ? 'נשלח' : 'SHIPPED') : (he ? 'ממתין' : 'PENDING')}
                          </span>
                        </span>
                        <span className="text-muted" style={{ fontSize: 12 }}>
                          <strong style={{ color: '#166534' }}>{s.handledSerials || 0}</strong>/{s.totalSerials} {he ? 'טופלו' : 'handled'} · {s.cartons.length} {he ? 'קרטונים' : 'cartons'}
                        </span>
                        {canEdit && (
                          <button className="btn btn-sm btn-outline" style={{ fontSize: 10, padding: '2px 6px' }}
                            onClick={() => bulkHandle(o.orderNo, s.sku, !allHandled)}
                            title={allHandled ? (he ? 'בטל טיפול בכל היחידות' : 'Unmark all as handled') : (he ? 'סמן את כל היחידות כטופלו' : 'Mark all as handled')}>
                            {allHandled ? '↩️ ' + (he ? 'בטל הכל' : 'Unmark all') : '✓ ' + (he ? 'סמן הכל' : 'Mark all')}
                          </button>
                        )}
                      </div>
                      {skuOpen && (
                        <div style={{ marginTop: 8, paddingInlineStart: 20 }}>
                          {s.cartons.map(c => {
                            const cartonKey = `${skuKey}::${c.masterCarton}`;
                            const cartonOpen = expandedCartons.has(cartonKey);
                            const cartonHandled = c.serials.filter(ser => ser.handled).length;
                            return (
                              <div key={c.masterCarton || 'nocarton'} style={{ marginBottom: 4 }}>
                                <div style={{ display: 'flex', gap: 10, alignItems: 'center', cursor: 'pointer', padding: '4px 8px', background: '#f8f9fa', borderRadius: 4 }}
                                  onClick={() => setExpandedCartons(prev => { const ns = new Set(prev); ns.has(cartonKey) ? ns.delete(cartonKey) : ns.add(cartonKey); return ns; })}>
                                  <span className={`arrow${cartonOpen ? ' open' : ''}`} style={{ fontSize: 10 }}>&#9654;</span>
                                  <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 12 }}>{c.masterCarton || '(no carton)'}</span>
                                  <div style={{ flex: 1 }} />
                                  <span className="text-muted" style={{ fontSize: 11 }}>
                                    <strong style={{ color: cartonHandled === c.serials.length ? '#166534' : '#92400e' }}>{cartonHandled}</strong>/{c.serials.length} {he ? 'טופלו' : 'handled'}
                                  </span>
                                </div>
                                {cartonOpen && (
                                  <div style={{ paddingInlineStart: 20, paddingBlock: 4 }}>
                                    {c.serials.map(ser => (
                                      <div key={ser.id} style={{ padding: '3px 0', color: '#444', display: 'flex', alignItems: 'center', gap: 8, fontSize: 11 }}>
                                        {canEdit ? (
                                          <input type="checkbox"
                                            checked={!!ser.handled}
                                            onChange={() => toggleHandled(ser.id, ser.handled)}
                                            style={{ cursor: 'pointer' }}
                                            title={ser.handled ? (he ? 'לחץ לבטל סימון' : 'Click to unmark') : (he ? 'לחץ לסמן כטופל' : 'Click to mark handled')}
                                          />
                                        ) : (
                                          <span style={{ fontSize: 11 }}>{ser.handled ? '✓' : '○'}</span>
                                        )}
                                        <span style={{ fontFamily: "'DM Mono', monospace", textDecoration: ser.handled ? 'line-through' : 'none', color: ser.handled ? '#16a34a' : '#444' }}>
                                          {ser.serial}
                                        </span>
                                        {ser.batchDate && <span className="text-muted" style={{ marginInlineStart: 'auto', fontSize: 10 }}>{new Date(ser.batchDate).toLocaleDateString('en-GB')}</span>}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// External Warehouse View — pallet-level inventory from supplier xlsx
// ═══════════════════════════════════════════════════════════════
export function ExternalWarehouseView({ canEdit, t, hasRole, setLogTab }) {
  const [pallets, setPallets] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterUploadId, setFilterUploadId] = useState('');
  const [showUploads, setShowUploads] = useState(false);
  const [editingMatchId, setEditingMatchId] = useState(null);
  const [matchCandidates, setMatchCandidates] = useState({});  // sku → candidates
  const [sortBy, setSortBy] = useState('opening');
  const [sortDir, setSortDir] = useState('desc');
  const [pullSelection, setPullSelection] = useState({});  // palletId → palletsToPull (number)
  const [pullNotes, setPullNotes] = useState('');
  const [submittingPull, setSubmittingPull] = useState(false);
  const isAdmin = hasRole('admin');
  const he = document.documentElement.dir === 'rtl';

  const fetchAll = () => {
    setLoading(true);
    Promise.all([
      api.get('/external-warehouse/pallets', { params: { search, uploadId: filterUploadId || undefined } }),
      api.get('/external-warehouse/uploads'),
    ])
      .then(([p, u]) => {
        setPallets(p.data.pallets || []);
        setUploads(u.data.uploads || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [filterUploadId]);

  const setPullCount = (palletId, max, value) => {
    let n = parseInt(value, 10);
    if (isNaN(n) || n < 0) n = 0;
    if (n > max) n = max;
    setPullSelection(prev => {
      const next = { ...prev };
      if (n > 0) next[palletId] = n;
      else delete next[palletId];
      return next;
    });
  };

  const downloadPullOrderExcel = async (poId, poNo) => {
    try {
      const r = await api.get(`/external-warehouse/pull-orders/${poId}/excel`, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pull-order-${poNo}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert((he ? 'שגיאה בהורדה: ' : 'Download failed: ') + (err.response?.data?.error || err.message));
    }
  };

  const submitPullOrder = async () => {
    const lines = Object.entries(pullSelection)
      .map(([palletId, palletsToPull]) => ({ palletId: Number(palletId), palletsToPull }))
      .filter(l => l.palletsToPull > 0);
    if (lines.length === 0) { alert(he ? 'לא נבחרו משטחים' : 'No pallets selected'); return; }
    const totalPallets = lines.reduce((a, l) => a + l.palletsToPull, 0);
    const msg = he
      ? `לייצר הזמנת משיכה עבור ${lines.length} שורות (${totalPallets} משטחים)?`
      : `Create pull order for ${lines.length} lines (${totalPallets} pallets)?`;
    if (!(await confirm({ body: msg }))) return;
    setSubmittingPull(true);
    try {
      const r = await api.post('/external-warehouse/pull-orders', { lines, notes: pullNotes || null });
      const { id, pullOrderNo } = r.data;
      setPullSelection({});
      setPullNotes('');
      await downloadPullOrderExcel(id, pullOrderNo);
      fetchAll();
      // Switch to pull orders tab so user sees the saved order
      if (setLogTab) setLogTab('pullOrders');
    } catch (err) {
      alert((he ? 'שגיאה ביצירת הזמנה: ' : 'Failed to create pull order: ') + (err.response?.data?.error || err.message));
    } finally {
      setSubmittingPull(false);
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    setUploading(true);
    try {
      const r = await api.post('/external-warehouse/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const { inserted, autoMatched, unknownBarcodes } = r.data;
      let msg = he
        ? `${inserted} שורות נטענו\nהותאמו אוטומטית ל־SI: ${autoMatched}`
        : `${inserted} rows loaded\nAuto-matched to SI: ${autoMatched}`;
      if (unknownBarcodes?.length) {
        msg += `\n${he ? '⚠ ברקודים לא מזוהים' : '⚠ Unknown barcodes'}: ${unknownBarcodes.join(', ')}`;
      }
      alert(msg);
      fetchAll();
    } catch (err) {
      alert((he ? 'שגיאה בהעלאה: ' : 'Upload failed: ') + (err.response?.data?.error || err.message));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const deleteUpload = async (id, filename) => {
    if (!(await confirm({ body: he ? `למחוק את ההעלאה "${filename}" וכל המשטחים שלה?` : `Delete upload "${filename}" and all its pallets?`, danger: true }))) return;
    try {
      await api.delete(`/external-warehouse/uploads/${id}`);
      fetchAll();
    } catch (err) {
      alert((he ? 'שגיאה במחיקה: ' : 'Delete failed: ') + (err.response?.data?.error || err.message));
    }
  };

  const openMatchEditor = async (palletId, sku) => {
    if (editingMatchId === palletId) { setEditingMatchId(null); return; }
    setEditingMatchId(palletId);
    if (sku && !matchCandidates[sku]) {
      try {
        const r = await api.get('/external-warehouse/si-candidates', { params: { sku } });
        setMatchCandidates(c => ({ ...c, [sku]: r.data.candidates || [] }));
      } catch {}
    }
  };

  const saveMatch = async (palletId, orderNo) => {
    try {
      await api.put(`/external-warehouse/pallets/${palletId}/match-si`, { orderNo: orderNo || null });
      setEditingMatchId(null);
      fetchAll();
    } catch (err) {
      alert((he ? 'שגיאה בעדכון: ' : 'Update failed: ') + (err.response?.data?.error || err.message));
    }
  };

  const toggleSort = (field) => {
    if (sortBy === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortDir('asc'); }
  };

  const filteredSorted = (() => {
    let arr = pallets;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      arr = arr.filter(p =>
        (p.barcode || '').toLowerCase().includes(q) ||
        (p.sku || '').toLowerCase().includes(q) ||
        (p.product_description || '').toLowerCase().includes(q) ||
        (p.matched_si_order_no || '').toLowerCase().includes(q) ||
        (p.goods_description || '').toLowerCase().includes(q)
      );
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    arr = [...arr].sort((a, b) => {
      const va = a[sortBy === 'opening' ? 'block_opening_date'
                  : sortBy === 'block' ? 'block_number'
                  : sortBy === 'product' ? 'product_description'
                  : sortBy === 'cartons' ? 'in_qty'
                  : sortBy === 'units' ? 'total_units'
                  : sortBy === 'si' ? 'matched_si_order_no'
                  : 'block_opening_date'] ?? '';
      const vb = b[sortBy === 'opening' ? 'block_opening_date'
                  : sortBy === 'block' ? 'block_number'
                  : sortBy === 'product' ? 'product_description'
                  : sortBy === 'cartons' ? 'in_qty'
                  : sortBy === 'units' ? 'total_units'
                  : sortBy === 'si' ? 'matched_si_order_no'
                  : 'block_opening_date'] ?? '';
      if (va === vb) return 0;
      if (va === '' || va == null) return 1;
      if (vb === '' || vb == null) return -1;
      return va < vb ? -1 * dir : 1 * dir;
    });
    return arr;
  })();

  const totalPallets = pallets.reduce((a, p) => a + (Number(p.pallet_count) || 0), 0);
  const totalCartons = pallets.reduce((a, p) => a + (Number(p.in_qty) || 0), 0);
  const totalUnits = pallets.reduce((a, p) => a + (Number(p.total_units) || 0), 0);
  const matchedCount = pallets.filter(p => p.matched_si_order_no).length;

  if (loading) return <div className="loading"><div className="spinner" />{t('common.loading')}</div>;

  const sortIcon = (field) => sortBy === field ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

  return (
    <div>
      {/* ═══ Upload + summary bar ═══ */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16, padding: 16, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
        {canEdit && (
          <label className="btn btn-primary" style={{ cursor: 'pointer', margin: 0 }}>
            {uploading ? (he ? 'מעלה...' : 'Uploading...') : (he ? '📤 העלה אקסל' : '📤 Upload Excel')}
            <input type="file" accept=".xlsx,.xls,.xlsb,.csv" onChange={onUpload} disabled={uploading} style={{ display: 'none' }} />
          </label>
        )}
        <button className="btn btn-secondary" onClick={() => setShowUploads(v => !v)}>
          {showUploads ? (he ? 'הסתר היסטוריה' : 'Hide history') : (he ? `היסטוריית העלאות (${uploads.length})` : `Upload history (${uploads.length})`)}
        </button>
        <input
          className="input"
          placeholder={he ? 'חפש לפי ברקוד / מק"ט / מוצר / SI ...' : 'Search by barcode / SKU / product / SI ...'}
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ maxWidth: 320, flex: 1 }}
        />
        <select className="input" value={filterUploadId} onChange={e => setFilterUploadId(e.target.value)} style={{ maxWidth: 240 }}>
          <option value="">{he ? 'כל ההעלאות' : 'All uploads'}</option>
          {uploads.map(u => (
            <option key={u.id} value={u.id}>
              {u.filename} ({new Date(u.uploaded_at).toLocaleDateString('en-GB')})
            </option>
          ))}
        </select>
        <div style={{ marginInlineStart: 'auto', display: 'flex', gap: 16, fontSize: 13, color: '#475569' }}>
          <span>📦 <b>{totalPallets}</b> {he ? 'משטחים' : 'pallets'}</span>
          <span>🧰 <b>{totalCartons.toLocaleString()}</b> {he ? 'קרטונים' : 'cartons'}</span>
          <span>📊 <b>{totalUnits.toLocaleString()}</b> {he ? 'יח׳' : 'units'}</span>
          <span style={{ color: matchedCount === pallets.length ? '#16a34a' : '#ea580c' }}>
            🔗 <b>{matchedCount}/{pallets.length}</b> {he ? 'הותאמו ל־SI' : 'matched'}
          </span>
        </div>
      </div>

      {/* ═══ Uploads history ═══ */}
      {showUploads && (
        <div style={{ marginBottom: 16, border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
          <table className="table" style={{ margin: 0 }}>
            <thead>
              <tr>
                <th>{he ? 'קובץ' : 'File'}</th>
                <th>{he ? 'הועלה ב' : 'Uploaded'}</th>
                <th>{he ? 'משתמש' : 'User'}</th>
                <th>{he ? 'שורות' : 'Rows'}</th>
                <th>{he ? 'משטחים' : 'Pallets'}</th>
                <th>{he ? 'יח׳' : 'Units'}</th>
                {isAdmin && <th></th>}
              </tr>
            </thead>
            <tbody>
              {uploads.length === 0 && <tr><td colSpan={isAdmin ? 7 : 6} style={{ textAlign: 'center', padding: 16, color: '#94a3b8' }}>{he ? 'אין העלאות' : 'No uploads yet'}</td></tr>}
              {uploads.map(u => (
                <tr key={u.id}>
                  <td>{u.filename}</td>
                  <td>{new Date(u.uploaded_at).toLocaleString('en-GB')}</td>
                  <td>{u.uploaded_by_username || '—'}</td>
                  <td>{u.row_count}</td>
                  <td>{u.pallet_count}</td>
                  <td>{Number(u.total_units || 0).toLocaleString()}</td>
                  {isAdmin && (
                    <td>
                      <button className="btn btn-danger btn-sm" onClick={() => deleteUpload(u.id, u.filename)}>
                        {he ? 'מחק' : 'Delete'}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ═══ Pallets table ═══ */}
      <div style={{ overflowX: 'auto' }}>
        <table className="table">
          <thead>
            <tr>
              <th onClick={() => toggleSort('opening')} style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>{he ? 'תאריך כניסה' : 'Entry date'}{sortIcon('opening')}</th>
              <th onClick={() => toggleSort('block')} style={{ cursor: 'pointer' }}>{he ? 'גוש' : 'Block'}{sortIcon('block')}</th>
              <th>{he ? 'ברקוד' : 'Barcode'}</th>
              <th>{he ? 'מק"ט' : 'SKU'}</th>
              <th onClick={() => toggleSort('product')} style={{ cursor: 'pointer' }}>{he ? 'מוצר' : 'Product'}{sortIcon('product')}</th>
              <th>{he ? 'תצורה' : 'Config'}</th>
              <th>{he ? 'משטחים' : 'Pallets'}</th>
              <th onClick={() => toggleSort('cartons')} style={{ cursor: 'pointer' }}>{he ? 'קרטונים' : 'Cartons'}{sortIcon('cartons')}</th>
              <th>{he ? 'יח׳/קרטון' : 'U/Carton'}</th>
              <th onClick={() => toggleSort('units')} style={{ cursor: 'pointer', textAlign: 'end' }}>{he ? 'סה"כ יח׳' : 'Total units'}{sortIcon('units')}</th>
              <th onClick={() => toggleSort('si')} style={{ cursor: 'pointer' }}>{he ? 'הזמנת SI' : 'SI Order'}{sortIcon('si')}</th>
              {canEdit && <th style={{ background: '#fef9c3' }}>{he ? 'למשיכה' : 'To pull'}</th>}
              {canEdit && <th></th>}
            </tr>
          </thead>
          <tbody>
            {filteredSorted.length === 0 && (
              <tr><td colSpan={canEdit ? 13 : 11} style={{ textAlign: 'center', padding: 32, color: '#94a3b8' }}>
                {he ? 'אין משטחים — העלה קובץ אקסל כדי להתחיל' : 'No pallets — upload an Excel file to start'}
              </td></tr>
            )}
            {filteredSorted.map(p => (
              <React.Fragment key={p.id}>
                <tr>
                  <td style={{ whiteSpace: 'nowrap' }}>{p.block_opening_date ? new Date(p.block_opening_date).toLocaleDateString('en-GB') : '—'}</td>
                  <td>{p.block_number || '—'}</td>
                  <td style={{ fontFamily: "'DM Mono', monospace", fontSize: 12 }}>{p.barcode || '—'}</td>
                  <td>{p.sku || <span style={{ color: '#dc2626' }}>{he ? 'לא מזוהה' : 'Unknown'}</span>}</td>
                  <td>{p.product_description || '—'}</td>
                  <td style={{ fontFamily: "'DM Mono', monospace", fontSize: 12 }}>{p.pallet_config || '—'}</td>
                  <td>{p.pallet_count || '—'}</td>
                  <td>{Number(p.in_qty || 0).toLocaleString()}</td>
                  <td>{p.units_per_carton || 1}</td>
                  <td style={{ textAlign: 'end', fontWeight: 600 }}>{Number(p.total_units || 0).toLocaleString()}</td>
                  <td>
                    {p.matched_si_order_no ? (
                      <span style={{ background: p.matched_manually ? '#dbeafe' : '#dcfce7', padding: '2px 6px', borderRadius: 4, fontSize: 12 }}>
                        {p.matched_si_order_no}
                        {p.matched_manually && ' ✏'}
                      </span>
                    ) : (
                      <span style={{ color: '#dc2626', fontSize: 12 }}>{he ? 'לא הותאם' : 'Unmatched'}</span>
                    )}
                  </td>
                  {canEdit && (
                    <td style={{ background: '#fef9c3', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <input
                          type="number"
                          min="0"
                          max={p.pallet_count || 0}
                          value={pullSelection[p.id] || ''}
                          onChange={e => setPullCount(p.id, p.pallet_count || 0, e.target.value)}
                          placeholder="0"
                          disabled={!p.pallet_count}
                          style={{ width: 56, padding: '4px 6px', border: '1px solid #cbd5e1', borderRadius: 4, textAlign: 'center', fontWeight: 600 }}
                        />
                        <span style={{ color: '#94a3b8', fontSize: 12 }}>/ {p.pallet_count || 0}</span>
                      </div>
                    </td>
                  )}
                  {canEdit && (
                    <td>
                      <button className="btn btn-sm btn-secondary" disabled={!p.sku} onClick={() => openMatchEditor(p.id, p.sku)}>
                        {editingMatchId === p.id ? (he ? 'סגור' : 'Close') : (he ? 'שנה' : 'Change')}
                      </button>
                    </td>
                  )}
                </tr>
                {editingMatchId === p.id && (
                  <tr>
                    <td colSpan={canEdit ? 13 : 11} style={{ background: '#f8fafc', padding: 12 }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 13, fontWeight: 600 }}>{he ? 'בחר הזמנת SI:' : 'Pick SI order:'}</span>
                        {(matchCandidates[p.sku] || []).length === 0 && (
                          <span style={{ color: '#94a3b8', fontSize: 13 }}>{he ? 'אין הזמנות SI עבור מק"ט זה' : 'No SI orders for this SKU'}</span>
                        )}
                        {(matchCandidates[p.sku] || []).map(c => (
                          <button
                            key={c.order_no}
                            className={`btn btn-sm ${p.matched_si_order_no === c.order_no ? 'btn-primary' : 'btn-secondary'}`}
                            onClick={() => saveMatch(p.id, c.order_no)}
                            title={`Order date: ${c.order_date ? new Date(c.order_date).toLocaleDateString('en-GB') : '—'} | Arrival: ${c.arrival_date ? new Date(c.arrival_date).toLocaleDateString('en-GB') : '—'}`}
                          >
                            {c.order_no} · {Number(c.quantity).toLocaleString()} {he ? 'יח׳' : 'units'}{c.arrival_date ? ` · ${new Date(c.arrival_date).toLocaleDateString('en-GB')}` : ''}
                          </button>
                        ))}
                        {p.matched_si_order_no && (
                          <button className="btn btn-sm btn-danger" onClick={() => saveMatch(p.id, null)}>
                            {he ? 'נקה התאמה' : 'Clear match'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* ═══ Sticky pull-order action bar ═══ */}
      {canEdit && Object.keys(pullSelection).length > 0 && (() => {
        const selectedPalletsTotal = Object.values(pullSelection).reduce((a, n) => a + Number(n || 0), 0);
        const selectedLines = Object.keys(pullSelection).length;
        const estUnits = Object.entries(pullSelection).reduce((a, [palletId, n]) => {
          const p = pallets.find(x => x.id === Number(palletId));
          if (!p || !p.pallet_count) return a;
          const perPallet = Number(p.in_qty || 0) / Number(p.pallet_count);
          return a + Math.round(perPallet * Number(n)) * Number(p.units_per_carton || 1);
        }, 0);
        return (
          <div style={{
            position: 'sticky', bottom: 16, marginTop: 16, background: '#1e293b', color: '#fff',
            padding: '14px 20px', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
            display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', zIndex: 10,
          }}>
            <span style={{ fontSize: 14 }}>
              📋 <b>{selectedLines}</b> {he ? 'שורות' : 'lines'} · 📦 <b>{selectedPalletsTotal}</b> {he ? 'משטחים' : 'pallets'} · 📊 ~<b>{estUnits.toLocaleString()}</b> {he ? 'יח׳ משוער' : 'est. units'}
            </span>
            <input
              className="input"
              placeholder={he ? 'הערות (אופציונלי)' : 'Notes (optional)'}
              value={pullNotes}
              onChange={e => setPullNotes(e.target.value)}
              style={{ flex: 1, minWidth: 200, color: '#1e293b' }}
            />
            <button className="btn btn-primary" disabled={submittingPull} onClick={submitPullOrder}>
              {submittingPull ? (he ? 'שולח...' : 'Submitting...') : (he ? '🚚 ייצר הזמנת משיכה + הורד אקסל' : '🚚 Create pull order + download Excel')}
            </button>
            <button className="btn btn-secondary" onClick={() => { setPullSelection({}); setPullNotes(''); }}>
              {he ? 'נקה בחירה' : 'Clear'}
            </button>
          </div>
        );
      })()}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Pull Orders View — withdrawals from central warehouse
// ═══════════════════════════════════════════════════════════════
export function PullOrdersView({ canEdit, t, hasRole }) {
  const [pullOrders, setPullOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [expanded, setExpanded] = useState(new Set());
  const [linesById, setLinesById] = useState({});
  const isAdmin = hasRole('admin');
  const he = document.documentElement.dir === 'rtl';

  const fetchAll = () => {
    setLoading(true);
    api.get('/external-warehouse/pull-orders')
      .then(r => setPullOrders(r.data.pullOrders || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { fetchAll(); }, []);

  const downloadExcel = async (poId, poNo) => {
    try {
      const r = await api.get(`/external-warehouse/pull-orders/${poId}/excel`, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pull-order-${poNo}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert((he ? 'שגיאה בהורדה: ' : 'Download failed: ') + (err.response?.data?.error || err.message));
    }
  };

  const cancelOrder = async (poId, poNo) => {
    if (!(await confirm({ body: he ? `לבטל את הזמנת המשיכה ${poNo}?` : `Cancel pull order ${poNo}?`, danger: true }))) return;
    try {
      await api.post(`/external-warehouse/pull-orders/${poId}/cancel`);
      fetchAll();
    } catch (err) {
      alert((he ? 'שגיאה בביטול: ' : 'Cancel failed: ') + (err.response?.data?.error || err.message));
    }
  };

  const fulfillOrder = async (poId, poNo) => {
    if (!(await confirm({
      title: he ? 'סימון משיכה' : 'Mark fulfilled',
      body: he
        ? `לסמן את ${poNo} כנמשך? פעולה זו תפחית את המשטחים מהמלאי החיצוני.`
        : `Mark ${poNo} as fulfilled? This will reduce the pallets from external warehouse stock.`,
    }))) return;
    try {
      await api.post(`/external-warehouse/pull-orders/${poId}/fulfill`);
      fetchAll();
    } catch (err) {
      alert((he ? 'שגיאה בסימון: ' : 'Fulfill failed: ') + (err.response?.data?.error || err.message));
    }
  };

  const deleteOrder = async (poId, poNo) => {
    if (!(await confirm({ body: he ? `למחוק לצמיתות את ${poNo}?` : `Permanently delete ${poNo}?`, danger: true }))) return;
    try {
      await api.delete(`/external-warehouse/pull-orders/${poId}`);
      fetchAll();
    } catch (err) {
      alert((he ? 'שגיאה במחיקה: ' : 'Delete failed: ') + (err.response?.data?.error || err.message));
    }
  };

  const toggleExpand = async (poId) => {
    const next = new Set(expanded);
    if (next.has(poId)) {
      next.delete(poId);
    } else {
      next.add(poId);
      if (!linesById[poId]) {
        try {
          const r = await api.get(`/external-warehouse/pull-orders/${poId}`);
          setLinesById(prev => ({ ...prev, [poId]: r.data.lines || [] }));
        } catch {}
      }
    }
    setExpanded(next);
  };

  const filtered = pullOrders.filter(po => {
    if (filterStatus && po.status !== filterStatus) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      if (!(po.pull_order_no || '').toLowerCase().includes(q) &&
          !(po.notes || '').toLowerCase().includes(q) &&
          !(po.created_by_username || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const totalPallets = filtered.reduce((a, po) => a + Number(po.total_pallets || 0), 0);
  const totalUnits = filtered.reduce((a, po) => a + Number(po.total_units || 0), 0);
  const pendingCount = pullOrders.filter(po => po.status === 'pending').length;
  const fulfilledCount = pullOrders.filter(po => po.status === 'fulfilled').length;
  const cancelledCount = pullOrders.filter(po => po.status === 'cancelled').length;

  if (loading) return <div className="loading"><div className="spinner" />{t('common.loading')}</div>;

  return (
    <div>
      {/* ═══ Toolbar + summary ═══ */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16, padding: 16, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
        <input
          className="input"
          placeholder={he ? 'חפש מס׳ הזמנה / הערות / משתמש...' : 'Search order # / notes / user...'}
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ maxWidth: 320, flex: 1 }}
        />
        <select className="input" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ maxWidth: 200 }}>
          <option value="">{he ? 'כל הסטטוסים' : 'All statuses'}</option>
          <option value="pending">{he ? 'ממתין' : 'Pending'}</option>
          <option value="fulfilled">{he ? 'נמשך' : 'Fulfilled'}</option>
          <option value="cancelled">{he ? 'בוטל' : 'Cancelled'}</option>
        </select>
        <div style={{ marginInlineStart: 'auto', display: 'flex', gap: 16, fontSize: 13, color: '#475569' }}>
          <span>📋 <b>{filtered.length}</b> {he ? 'הזמנות' : 'orders'}</span>
          <span>📦 <b>{totalPallets}</b> {he ? 'משטחים' : 'pallets'}</span>
          <span>📊 <b>{totalUnits.toLocaleString()}</b> {he ? 'יח׳' : 'units'}</span>
          <span style={{ color: '#ea580c' }}>⏳ <b>{pendingCount}</b> {he ? 'ממתין' : 'pending'}</span>
          <span style={{ color: '#16a34a' }}>✓ <b>{fulfilledCount}</b> {he ? 'נמשך' : 'fulfilled'}</span>
          {cancelledCount > 0 && <span style={{ color: '#94a3b8' }}>✕ <b>{cancelledCount}</b> {he ? 'בוטל' : 'cancelled'}</span>}
        </div>
      </div>

      {/* ═══ Pull orders table ═══ */}
      <div style={{ overflowX: 'auto' }}>
        <table className="table">
          <thead>
            <tr>
              <th></th>
              <th>{he ? 'מס׳ הזמנה' : 'Order #'}</th>
              <th>{he ? 'תאריך' : 'Date'}</th>
              <th>{he ? 'משתמש' : 'User'}</th>
              <th>{he ? 'שורות' : 'Lines'}</th>
              <th>{he ? 'משטחים' : 'Pallets'}</th>
              <th>{he ? 'יח׳ משוער' : 'Est. units'}</th>
              <th>{he ? 'סטטוס' : 'Status'}</th>
              <th>{he ? 'הערות' : 'Notes'}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={10} style={{ textAlign: 'center', padding: 32, color: '#94a3b8' }}>
                {he ? 'אין הזמנות משיכה — צור הזמנה מטאב "מחסן חיצוני"' : 'No pull orders — create one from the External Warehouse tab'}
              </td></tr>
            )}
            {filtered.map(po => {
              const statusColor = po.status === 'fulfilled' ? '#16a34a' : po.status === 'cancelled' ? '#94a3b8' : '#ea580c';
              const statusLabel = he
                ? (po.status === 'fulfilled' ? 'נמשך' : po.status === 'cancelled' ? 'בוטל' : 'ממתין')
                : (po.status === 'fulfilled' ? 'Fulfilled' : po.status === 'cancelled' ? 'Cancelled' : 'Pending');
              const isExpanded = expanded.has(po.id);
              const lines = linesById[po.id] || [];
              return (
                <React.Fragment key={po.id}>
                  <tr style={{ cursor: 'pointer' }} onClick={() => toggleExpand(po.id)}>
                    <td style={{ width: 30, textAlign: 'center' }}>{isExpanded ? '▼' : '▶'}</td>
                    <td style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, fontWeight: 600 }}>{po.pull_order_no}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{new Date(po.created_at).toLocaleString('en-GB')}</td>
                    <td>{po.created_by_username || '—'}</td>
                    <td>{po.line_count}</td>
                    <td>{po.total_pallets}</td>
                    <td>{Number(po.total_units || 0).toLocaleString()}</td>
                    <td><span style={{ background: statusColor + '22', color: statusColor, padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600 }}>{statusLabel}</span></td>
                    <td style={{ fontSize: 12, color: '#64748b', maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{po.notes || '—'}</td>
                    <td onClick={e => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                      <button className="btn btn-sm btn-secondary" onClick={() => downloadExcel(po.id, po.pull_order_no)}>
                        📥 {he ? 'אקסל' : 'Excel'}
                      </button>
                      {po.status === 'pending' && canEdit && (
                        <>
                          <button className="btn btn-sm btn-primary" style={{ marginInlineStart: 4 }} onClick={() => fulfillOrder(po.id, po.pull_order_no)}>
                            ✓ {he ? 'נמשך' : 'Fulfilled'}
                          </button>
                          <button className="btn btn-sm btn-danger" style={{ marginInlineStart: 4 }} onClick={() => cancelOrder(po.id, po.pull_order_no)}>
                            {he ? 'בטל' : 'Cancel'}
                          </button>
                        </>
                      )}
                      {po.status === 'cancelled' && isAdmin && (
                        <button className="btn btn-sm btn-danger" style={{ marginInlineStart: 4 }} onClick={() => deleteOrder(po.id, po.pull_order_no)}>
                          🗑 {he ? 'מחק' : 'Delete'}
                        </button>
                      )}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan={10} style={{ background: '#f8fafc', padding: 0 }}>
                        <table className="table" style={{ margin: 0, fontSize: 12 }}>
                          <thead>
                            <tr style={{ background: '#e2e8f0' }}>
                              <th>{he ? 'גוש' : 'Block'}</th>
                              <th>{he ? 'ברקוד' : 'Barcode'}</th>
                              <th>{he ? 'מק"ט' : 'SKU'}</th>
                              <th>{he ? 'מוצר' : 'Product'}</th>
                              <th>{he ? 'תצורה' : 'Config'}</th>
                              <th>{he ? 'סה״כ משטחים' : 'Total pallets'}</th>
                              <th>{he ? 'משטחים למשיכה' : 'Pallets pulled'}</th>
                              <th>{he ? 'קרטונים משוער' : 'Est. cartons'}</th>
                              <th>{he ? 'יח׳/קרטון' : 'U/Carton'}</th>
                              <th>{he ? 'יח׳ משוער' : 'Est. units'}</th>
                              <th>{he ? 'הזמנת SI' : 'SI Order'}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {lines.length === 0 && <tr><td colSpan={11} style={{ textAlign: 'center', padding: 12, color: '#94a3b8' }}>{he ? 'טוען...' : 'Loading...'}</td></tr>}
                            {lines.map(l => (
                              <tr key={l.id}>
                                <td>{l.snapshot_block_number || '—'}</td>
                                <td style={{ fontFamily: "'DM Mono', monospace" }}>{l.snapshot_barcode || '—'}</td>
                                <td>{l.snapshot_sku || '—'}</td>
                                <td>{l.snapshot_product_description || '—'}</td>
                                <td style={{ fontFamily: "'DM Mono', monospace" }}>{l.snapshot_pallet_config || '—'}</td>
                                <td>{l.snapshot_total_pallets || '—'}</td>
                                <td style={{ fontWeight: 600 }}>{l.pallets_to_pull}</td>
                                <td>{l.estimated_cartons || 0}</td>
                                <td>{l.snapshot_units_per_carton || 1}</td>
                                <td style={{ fontWeight: 600 }}>{Number(l.estimated_units || 0).toLocaleString()}</td>
                                <td>{l.snapshot_si_order_no ? <span style={{ background: '#dcfce7', padding: '2px 6px', borderRadius: 4 }}>{l.snapshot_si_order_no}</span> : '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
